/**
 * Canonical 5-axis structural framework used to classify every firm.
 *
 * Definitions come from landing_pages/business-os.html and
 * landing_pages/guides/the-future/the-cyber-economy-fork.html. Per-firm
 * values live on firm.framework[<key>] in data/firms/<slug>.json.
 */

export type AxisKey =
  | "clearing_layer"
  | "verification"
  | "surviving_moats"
  | "compression_risk"
  | "fork";

export interface AxisDef {
  key: AxisKey;
  label: string;
  definition: string;
  example?: string;
  source: string;
}

export const AXIS_DEFS: AxisDef[] = [
  {
    key: "clearing_layer",
    label: "Clearing layer",
    definition:
      "Where the canonical record of a transaction lives, and who owns the rails the firm trades on. Owning the clearing layer means owning settlement, identity, and the IOU. Renting it means paying rent to whoever does.",
    example: "Visa owns card-payment rails. A coffee shop rents them at ~2% of each swipe.",
    source: "5-axis framework, landing_pages/business-os.html",
  },
  {
    key: "verification",
    label: "Verification",
    definition:
      "How the record on the clearing layer gets trusted: manual sign-off, third-party audit, regulator, cryptographic proof. Sets how cheap or expensive it is for the firm to assert truth and survive a challenge.",
    example: "Defense primes: classified audit, DCMA, ITAR / EAR controls.",
    source: "5-axis framework, landing_pages/business-os.html",
  },
  {
    key: "surviving_moats",
    label: "Surviving moats",
    definition:
      "Which of the six structural moats still hold when code is nearly free: scale, network effects, switching costs, distribution, taste, vertical integration. Names the ones AI does not erode.",
    example: "Costco: scale + member distribution + curation (taste). Code price does not touch any of them.",
    source: "Six moats framework, landing_pages/business-os.html",
  },
  {
    key: "compression_risk",
    label: "Compression risk",
    definition:
      "Which roles in the firm AI compresses out of the org. Middle-office (proposals, claims, support, basic legal) compresses fastest. Field engineering, physical ops, and cleared workforce compress slowest.",
    example: "Insurance: adjusters and underwriters compress hard; field appraisers and litigators do not.",
    source: "Role-compression mapping, landing_pages/business-os.html",
  },
  {
    key: "fork",
    label: "Fork",
    definition:
      "Which side of the Cyber-economy fork the firm sits on. Cyber-economy: open, cryptographically verifiable, externalities published. Cyber-slavery: closed, opaque verification, externalities hidden. Some firms are the rails operator; some are captive on someone else's rails.",
    example:
      "NVIDIA is the rails operator for AI compute (cyber-economy). A defense prime is a cleared-rail captive (sealed-by-law cyber-slavery).",
    source: "Cyber-economy fork guide, landing_pages/guides/the-future/the-cyber-economy-fork.html",
  },
];
